import React from "react";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { fetchDispatchDayContext } from "../lib/dataManagerApi.js";
import { resolveDispatchDayStatus } from "../utils/dispatchDayControls.js";
import {
  shouldRevalidateDataManagerOrdersFromPlanningChange,
  subscribePlanningStateChange,
} from "../utils/planningCrossViewSync.js";

export default function DataManagerDispatchDayNotice({ dispatchDate = "" }) {
  const [state, setState] = React.useState(null);

  React.useEffect(() => {
    if (!dispatchDate) return undefined;
    let disposed = false;
    let revision = 0;

    async function refresh() {
      const requestRevision = ++revision;
      setState({ dispatchDate, status: "loading" });
      try {
        // Read fresh official state; the calendar cache may predate a close/reopen.
        const payload = await fetchDispatchDayContext();
        if (disposed || requestRevision !== revision) return;
        const days = payload?.dispatch_days ?? payload?.dispatchDays;
        const day = Array.isArray(days)
          ? days.find((entry) => (entry.dispatch_date ?? entry.dispatchDate) === dispatchDate)
          : null;
        setState({ dispatchDate, status: resolveDispatchDayStatus(day).key });
      } catch {
        if (!disposed && requestRevision === revision) {
          setState({ dispatchDate, status: "unavailable" });
        }
      }
    }

    const handleFocus = () => {
      if (document.visibilityState !== "hidden") void refresh();
    };
    const unsubscribe = subscribePlanningStateChange((detail) => {
      if (shouldRevalidateDataManagerOrdersFromPlanningChange(detail)) void refresh();
    });
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleFocus);
    void refresh();
    return () => {
      disposed = true;
      unsubscribe();
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleFocus);
    };
  }, [dispatchDate]);

  if (!dispatchDate) return null;
  const status = state?.dispatchDate === dispatchDate ? state.status : "loading";
  if (status !== "cloture" && status !== "unavailable" && status !== "loading") return null;

  return (
    <div role="status" className="mt-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] leading-4">
      {status === "cloture" ? (
        <>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-[4px] border border-red-200 bg-red-50 px-2 py-1 font-bold text-red-800">
            <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Journée fermée
          </span>
          <span className="min-w-0 text-slate-600">
            Données de dispatch en lecture seule. Les tonnages et les opérations de facturation restent accessibles selon vos permissions.
          </span>
        </>
      ) : (
        <span className="text-slate-600">
          {status === "loading" ? "Vérification de l’état de la journée…" : "État de la journée indisponible."}
        </span>
      )}
    </div>
  );
}
